import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design-system';
import { ErrorPanel } from '@/patterns';
import { isApiError } from '@/shared/api';

export class ForbiddenError extends Error {
  constructor(
    readonly permission: string,
    readonly area: string,
  ) {
    super(`Missing permission ${permission}`);
    this.name = 'ForbiddenError';
  }
}

/** In-shell "No access": names the area and the missing permission. No redirect. */
export function ForbiddenView({ area, permission }: { area: string; permission: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex max-w-xl flex-col gap-2 px-(--content-padding) py-8">
      <h1 className="text-h1 text-ink">{t('errors.forbiddenTitle')}</h1>
      <p className="text-body text-ink-secondary">{t('errors.forbiddenBody', { area, permission })}</p>
      <div className="pt-2">
        <Button asChild size="sm">
          <Link to="/dashboard">{t('errors.goToDashboard')}</Link>
        </Button>
      </div>
    </div>
  );
}

export function NotFoundView({ reference }: { reference?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex max-w-xl flex-col gap-2 px-(--content-padding) py-8">
      <h1 className="text-h1 text-ink">{t('errors.notFoundTitle')}</h1>
      <p className="text-body text-ink-secondary">{reference ? t('errors.recordNotFound', { reference }) : t('errors.notFoundBody')}</p>
      <div className="pt-2">
        <Button asChild size="sm">
          <Link to="/dashboard">{t('errors.goToDashboard')}</Link>
        </Button>
      </div>
    </div>
  );
}

/** Route error boundary content rendered inside the shell (or a tab). */
export function RouteErrorView({ error, reset }: { error: unknown; reset?: () => void }) {
  if (error instanceof ForbiddenError) return <ForbiddenView area={error.area} permission={error.permission} />;
  if (isApiError(error) && error.isForbidden) return <ForbiddenView area="" permission={error.detailText('permission')} />;
  if (isApiError(error) && error.isNotFound) return <NotFoundView />;
  return (
    <div className="px-(--content-padding) py-6">
      <ErrorPanel error={error} onRetry={reset} />
    </div>
  );
}
